import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type NewsletterSubscriberRow = {
  id: number
  storeId: number
  email: string
  status: string
  source: string
  subscribedAt: string
  unsubscribedAt: string | null
  createdAt: string
}

export async function fetchNewsletterStats(storeId: number): Promise<{ subscribedCount: number }> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${storeId}/newsletter/subscribers/stats`)
    return assertSuccess(data) as { subscribedCount: number }
  } catch (e) {
    throw apiError(e, 'Failed to load newsletter stats')
  }
}

export async function fetchNewsletterSubscribers(
  storeId: number,
  params: {
    page?: number
    limit?: number
    q?: string
    status?: string
    from?: string
    to?: string
  } = {},
): Promise<{
  items: NewsletterSubscriberRow[]
  total: number
  page: number
  limit: number
}> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${storeId}/newsletter/subscribers`, {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 25,
        q: params.q || undefined,
        status: params.status || undefined,
        from: params.from || undefined,
        to: params.to || undefined,
      },
    })
    return assertSuccess(data) as {
      items: NewsletterSubscriberRow[]
      total: number
      page: number
      limit: number
    }
  } catch (e) {
    throw apiError(e, 'Failed to load subscribers')
  }
}

export async function exportNewsletterSubscribersCsv(
  storeId: number,
  params: { q?: string; status?: string; from?: string; to?: string } = {},
): Promise<Blob> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${storeId}/newsletter/subscribers/export`, {
      params: {
        q: params.q || undefined,
        status: params.status || undefined,
        from: params.from || undefined,
        to: params.to || undefined,
      },
      responseType: 'blob',
    })
    return data as Blob
  } catch (e) {
    throw apiError(e, 'Failed to export subscribers')
  }
}
